/**
 * @fileoverview Floating label input component.
 * @summary Input field with floating label that moves up when focused or filled.
 *
 * @package KaraoNow
 * @module apps/front/src/components/FloatingLabelInput
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FloatingLabelInputProps {
  id: string
  name: string
  type?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  label: string
  required?: boolean
  optional?: boolean
  error?: string
  autoComplete?: string
  disabled?: boolean
  min?: string // For type="date"
  max?: string // For type="date"
}

/**
 * @summary Floating label input component.
 * @param {FloatingLabelInputProps} props - Component props.
 * @returns {React.ReactElement} Floating label input.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function FloatingLabelInput({
  id,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  label,
  required = false,
  optional = false,
  error,
  autoComplete,
  disabled = false,
  min,
  max,
}: FloatingLabelInputProps) {
  const { t } = useTranslation()
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = value.length > 0
  // For date and time inputs, always float the label to avoid overlap with browser's native UI
  const alwaysFloat = type === 'date' || type === 'time'
  const isFloating = alwaysFloat || isFocused || hasValue

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    onBlur?.(e)
  }

  return (
    <div className="floating-label-wrapper">
      <div className={`floating-label-container ${isFloating ? 'floating-label-active' : ''} ${error ? 'floating-label-error' : ''}`}>
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          required={required}
          autoComplete={autoComplete}
          disabled={disabled}
          min={min}
          max={max}
          className={`floating-label-input ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
        <label
          htmlFor={id}
          className={`truncate floating-label ${isFloating ? isFocused ? 'floating-label-up-focused' : 'floating-label-up' : 'pr-6 w-full'}`}
        >
          {label}
          {required && <span className="floating-label-required"> *</span>}
          {optional && !required && <span className="floating-label-optional"> ({t('auth.optional')})</span>}
        </label>
      </div>
      {error && (
        <p className="floating-label-error-message">{error}</p>
      )}
    </div>
  )
}
